// Danh sách "Đã gửi" của trang Gửi thông báo (docs/KE_HOACH_GUI_THONG_BAO.md §5): ngày, loại, tiêu đề, (người gửi),
// số người nhận, số đã xem, Xem / Thu hồi. Máy tính: bảng; điện thoại: thẻ dọc (DataTable theo data-viewport).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Section } from '@/components/ui/section'
import { LoadError } from '@/components/ui/data-notice'
import { confirm } from '@/components/ui/confirm'
import DataTable, { type DataTableColumn } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog'
import { AnnouncementView, KindBadge, announceKind } from './AnnouncementView'
import { fmtDate, fmtDateTime, fmtNum, type AnnItem, type AnnList, type AnnounceApi } from './types'

const LIMIT = 20

const errText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback)

function RecalledPill() {
  return <span className="inline-flex shrink-0 items-center rounded-full bg-paperSoft px-2 py-0.5 text-xs font-medium text-inkSoft">Đã thu hồi</span>
}

export function SentAnnouncements({ api, showSender = false }: { api: AnnounceApi; showSender?: boolean }) {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [viewing, setViewing] = useState<AnnItem | null>(null)
  const q = useQuery({
    queryKey: [api.area, 'announcements', page],
    queryFn: () => api.get<AnnList>(`${api.base}?page=${page}&limit=${LIMIT}`),
  })
  const rows = q.data?.items ?? []
  const total = q.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / LIMIT))

  const recall = useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean; removed: number }>(`${api.base}/${id}/recall`),
    onSuccess: () => {
      toast.success('Đã thu hồi')
      setViewing(null)
      qc.invalidateQueries({ queryKey: [api.area, 'announcements'] })
    },
    onError: (e) => toast.error(errText(e, 'Không thu hồi được')),
  })

  async function askRecall(a: AnnItem) {
    const ok = await confirm({
      title: 'Thu hồi thông báo này?',
      description: `Gỡ khỏi hộp thư của ${fmtNum(a.recipientCount)} người.`,
      confirmLabel: 'Thu hồi',
      tone: 'danger',
    })
    if (ok) recall.mutate(a.id)
  }

  const actions = (a: AnnItem, mobile: boolean) => (
    <div className={mobile ? 'flex gap-2' : 'flex justify-end gap-1.5'}>
      <Button type="button" size="sm" variant="outline" className={mobile ? 'flex-1' : undefined} onClick={() => setViewing(a)}>Xem</Button>
      {a.canRecall && (
        <Button type="button" size="sm" variant="outline" className={mobile ? 'flex-1 text-danger' : 'text-danger'} disabled={recall.isPending} onClick={() => askRecall(a)}>
          Thu hồi
        </Button>
      )}
    </div>
  )

  // Bảng máy tính: Ngày · Loại · Tiêu đề · (Người gửi) · Người nhận · Đã xem · nút. Thẻ điện thoại xếp theo priority.
  const columns: DataTableColumn<AnnItem>[] = [
    { key: 'date', header: 'Ngày', priority: 'secondary', cell: (a) => <span className="whitespace-nowrap tabular-nums">{fmtDateTime(a.createdAt)}</span> },
    { key: 'kind', header: 'Loại', cell: (a) => <KindBadge kind={announceKind(a.kind)} />, hideOnMobile: true },
    {
      key: 'title',
      header: 'Tiêu đề',
      priority: 'primary',
      cell: (a) => (
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate text-sm font-medium text-ink" title={a.title}>{a.title}</span>
          {a.recalledAt && <RecalledPill />}
        </span>
      ),
      mobileCell: (a) => (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <KindBadge kind={announceKind(a.kind)} />
            {a.recalledAt && <RecalledPill />}
          </div>
          <p className="break-words text-sm font-medium text-ink">{a.title}</p>
        </div>
      ),
      cellClassName: 'max-w-[22rem]',
    },
    ...(showSender
      ? [{ key: 'sender', header: 'Người gửi', priority: 'secondary' as const, cell: (a: AnnItem) => <span className="text-inkSoft">{a.senderName ?? '—'}</span> }]
      : []),
    { key: 'recipients', header: 'Người nhận', align: 'right', cell: (a) => <span className="tabular-nums">{fmtNum(a.recipientCount)}</span> },
    { key: 'read', header: 'Đã xem', align: 'right', cell: (a) => <span className="tabular-nums">{fmtNum(a.readCount)}</span> },
    {
      key: 'actions',
      header: <span className="sr-only">Thao tác</span>,
      priority: 'action',
      align: 'right',
      cell: (a) => actions(a, false),
      mobileCell: (a) => actions(a, true),
    },
  ]

  return (
    <Section
      title="Đã gửi"
      action={
        pages > 1 ? (
          <div className="flex items-center gap-1 text-xs tabular-nums text-muted">
            <Button type="button" variant="ghost" size="icon" aria-label="Trang trước" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft />
            </Button>
            <span>{page}/{pages}</span>
            <Button type="button" variant="ghost" size="icon" aria-label="Trang sau" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>
              <ChevronRight />
            </Button>
          </div>
        ) : null
      }
    >
      {q.isError ? (
        <LoadError onRetry={() => q.refetch()} />
      ) : (
        <DataTable columns={columns} data={rows} rowKey={(a) => a.id} loading={q.isLoading} emptyLabel="Chưa có thông báo" indexStart={(page - 1) * LIMIT + 1} />
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogTitle className="sr-only">{viewing?.title ?? ''}</DialogTitle>
          {viewing && (
            <>
              <AnnouncementView
                className="pr-8"
                a={{ kind: announceKind(viewing.kind), title: viewing.title, message: viewing.body, sender: viewing.senderName, createdAt: viewing.createdAt, url: viewing.linkUrl }}
              />
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Stat label="Đơn vị" value={fmtNum(viewing.unitCount)} />
                <Stat label="Người nhận" value={fmtNum(viewing.recipientCount)} />
                <Stat label="Đã xem" value={fmtNum(viewing.readCount)} />
                {viewing.expiresAt && <Stat label="Hết hạn" value={fmtDate(viewing.expiresAt)} />}
                {viewing.recalledAt && <Stat label="Đã thu hồi" value={fmtDateTime(viewing.recalledAt)} />}
              </dl>
            </>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            {viewing?.canRecall && (
              <Button type="button" variant="outline" className="text-danger" disabled={recall.isPending} onClick={() => viewing && askRecall(viewing)}>
                Thu hồi
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => setViewing(null)}>Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 tabular-nums text-ink">{value}</dd>
    </div>
  )
}
